import { getFirestore, doc, setDoc, getDoc } from 'firebase/firestore'
import { UserAvailability, AvailabilitySnapshot } from '../types/availability'
import { toSnapshot } from './availability'
import { ENABLE_AVAILABILITY } from '../config/flags'

const db = getFirestore()

/**
 * Load user availability from Firestore
 */
export async function loadUserAvailability(uid: string): Promise<UserAvailability | null> {
  if (!ENABLE_AVAILABILITY) {
    console.log('Availability feature is disabled')
    return null
  }
  
  try {
    const availabilityRef = doc(db, 'userAvailability', uid)
    const availabilitySnap = await getDoc(availabilityRef)
    
    if (availabilitySnap.exists()) {
      return availabilitySnap.data() as UserAvailability
    }
    
    return null
  } catch (error) {
    console.error('Error loading user availability:', error)
    return null
  }
}

/**
 * Save user availability to Firestore
 */
export async function saveUserAvailability(uid: string, availability: Partial<UserAvailability>): Promise<void> {
  if (!ENABLE_AVAILABILITY) {
    console.log('Availability feature is disabled, skipping save')
    return
  }
  
  try {
    const now = Date.now()
    
    // Get current availability first
    const currentAvailability = await loadUserAvailability(uid)
    
    const availabilityData: UserAvailability = {
      uid,
      tz: availability.tz || currentAvailability?.tz || 'Europe/Amsterdam',
      callEnabled: availability.callEnabled ?? currentAvailability?.callEnabled ?? true,
      videoEnabled: availability.videoEnabled ?? currentAvailability?.videoEnabled ?? true,
      windows: availability.windows || currentAvailability?.windows || [],
      defaultDur: availability.defaultDur || currentAvailability?.defaultDur || 30,
      officeHours: availability.officeHours || currentAvailability?.officeHours,
      updatedAt: now
    }
    
    // Save to userAvailability collection
    await setDoc(doc(db, 'userAvailability', uid), availabilityData, { merge: true })
    
    // Update snapshot in profiles collection
    const snapshot: AvailabilitySnapshot = toSnapshot(availabilityData)
    await setDoc(doc(db, 'profiles', uid), { availability: snapshot }, { merge: true })
    
    console.log('✅ User availability saved successfully')
  } catch (error) {
    console.error('❌ Error saving user availability:', error)
    throw error
  }
}

/**
 * Load availability snapshot from profile
 */
export async function loadAvailabilitySnapshot(uid: string): Promise<AvailabilitySnapshot | null> {
  if (!ENABLE_AVAILABILITY) {
    console.log('Availability feature is disabled')
    return null
  }
  
  try {
    const profileRef = doc(db, 'profiles', uid)
    const profileSnap = await getDoc(profileRef)
    
    if (profileSnap.exists()) {
      const profileData = profileSnap.data()
      return profileData.availability || null
    }
    
    return null
  } catch (error) {
    console.error('Error loading availability snapshot:', error)
    return null
  }
}
